/**
 * useClinicalHud3LearnerProfile — one-time Teaching sign-up answer.
 *
 * hasAnswered is true when clinicalHud3LearnerProfile/{uid} exists.
 * Does NOT check Live grant, attestation, roles, or clinicalHudAccess.
 */
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "hooks/useAuth";
import { db } from "services/firebase";
import { CLINICAL_HUD3_LEARNER_PROFILE_COLLECTION } from "./clinicalHud3Constants";

export function useClinicalHud3LearnerProfile() {
  const { user, userLoading } = useAuth();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);

  const uid = user?.uid ?? null;
  const isAnonymous = Boolean(user?.isAnonymous);
  const isRealAccount = Boolean(uid) && !isAnonymous;

  const refresh = useCallback(() => {
    setReloadToken((n) => n + 1);
  }, []);

  useEffect(() => {
    if (userLoading) {
      setProfileLoading(true);
      return undefined;
    }

    if (!isRealAccount) {
      setProfile(null);
      setProfileLoading(false);
      return undefined;
    }

    let cancelled = false;
    setProfileLoading(true);

    db.collection(CLINICAL_HUD3_LEARNER_PROFILE_COLLECTION)
      .doc(uid)
      .get()
      .then((snap) => {
        if (cancelled) return;
        setProfile(snap.exists ? snap.data() : null);
        setProfileLoading(false);
      })
      .catch((err) => {
        console.warn("[useClinicalHud3LearnerProfile] read failed", err);
        if (cancelled) return;
        setProfile(null);
        setProfileLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [uid, isRealAccount, userLoading, reloadToken]);

  const loading = Boolean(userLoading || (isRealAccount && profileLoading));
  const hasAnswered = Boolean(!loading && profile);

  return {
    loading,
    profile: loading ? null : profile,
    hasAnswered: loading ? false : hasAnswered,
    refresh,
  };
}

export default useClinicalHud3LearnerProfile;
